import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Typography, Badge, Button, Space, Image, Descriptions } from "antd";
import { User, Mail, Calendar, Shield, Lock, Unlock, Edit, Key } from "react-feather";

const { Title, Text } = Typography;

// Mock data
const mockUser = {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john@example.com',
    username: 'johndoe',
    externalRef: 'EXT-123',
    createdDate: '2025-01-01',
    isSuperAdmin: true,
    roleType: 'maker',
    status: 'active',
    isLoggedIn: false,
    avatar: 'https://via.placeholder.com/140', // Dummy avatar
};

const ViewModuleUser = () => {
    const { id } = useParams();
    const [user, setUser] = useState(mockUser);

    const handleDeactivate = () => {
        setUser(prev => ({ ...prev, status: 'inactive' }));
    };

    const handleUnlock = () => {
        console.log('Unlock user');
    };

    const handleUpdate = () => {
        console.log('Update user');
    };

    const handlePasswordReset = () => {
        console.log('Reset password');
    };

    const getStatusColor = (status) => {
        return status === 'active' ? '#52c41a' : '#ff4d4f';
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
            {/* Header Section */}
            <Card 
                style={{ 
                    marginBottom: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: 'none'
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div>
                            <Text type="secondary" style={{ fontSize: '14px' }}>User Profile</Text>
                            <Title level={4} style={{ margin: 0, color: '#1890ff', fontWeight: 600 }}>
                                {`${user.firstName} ${user.lastName}`}
                            </Title>
                        </div>
                        <Badge 
                            color={getStatusColor(user.status)}
                            text={
                                <span style={{ 
                                    color: getStatusColor(user.status),
                                    fontWeight: 500,
                                    textTransform: 'capitalize'
                                }}>
                                    {user.status}
                                </span>
                            }
                        />
                    </div>
                    <Space size="middle">
                        <Button 
                            type="primary" 
                            danger 
                            onClick={handleDeactivate}
                            icon={<Lock size={16} />}
                            disabled={user.status === 'inactive'}
                        >
                            Deactivate User
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={handleUnlock}
                            icon={<Unlock size={16} />}
                        >
                            Unlock User
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={handleUpdate}
                            icon={<Edit size={16} />}
                        >
                            Update User
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={handlePasswordReset}
                            icon={<Key size={16} />}
                        >
                            Password Reset
                        </Button>
                    </Space>
                </div>
            </Card>

            {/* User Details Section */}
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                {/* User Profile Card */}
                <Card 
                    style={{ 
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                        border: 'none',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                        color: 'white',
                        minWidth: '320px',
                        flex: '0 0 auto'
                    }}
                >
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        {/* Enhanced Avatar Section */}
                        <div style={{ 
                            position: 'relative',
                            display: 'inline-block',
                            marginBottom: '20px'
                        }}>
                            <div style={{ 
                                width: '140px', 
                                height: '140px', 
                                borderRadius: '50%', 
                                overflow: 'hidden',
                                margin: '0 auto',
                                border: '5px solid rgba(255,255,255,0.3)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                position: 'relative'
                            }}>
                                <Image 
                                    width={140} 
                                    height={140}
                                    src={user.avatar} 
                                    style={{ 
                                        objectFit: 'cover',
                                        filter: 'brightness(1.05) contrast(1.05)'
                                    }}
                                    fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDE0MCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxNDAiIGhlaWdodD0iMTQwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik07MCA5MEM4MS4wNDU3IDkwIDkwIDgxLjA0NTcgOTAgNzBDOTAgNTguOTU0MyA4MS4wNDU3IDUwIDcwIDUwQzU4Ljk1NDMgNTAgNTAgNTguOTU0MyA1MCA3MEM1MCA4MS4wNDU3IDU4Ljk1NDMgOTAgNzAgOTBaIiBmaWxsPSIjQzRDNEM0Ii8+CjxwYXRoIGQ9Ik0zMCAxMTBDMzAgMTAwIDUwIDkwIDcwIDkwQzkwIDkwIDExMCAxMDAgMTEwIDExMFYxMjBIMzBWMTEwWiIgZmlsbD0iI0M0QzRDNCIvPgo8L3N2Zz4K"
                                />
                            </div>
                            {/* Status indicator */}
                            <div style={{
                                position: 'absolute',
                                bottom: '10px',
                                right: '10px',
                                width: '24px',
                                height: '24px',
                                backgroundColor: getStatusColor(user.status),
                                border: '3px solid white',
                                borderRadius: '50%',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }} />
                        </div>
                        
                        {/* User Name */}
                        <Title level={3} style={{ 
                            margin: '0 0 8px 0', 
                            color: 'white',
                            fontWeight: 600,
                            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            {`${user.firstName} ${user.lastName}`}
                        </Title>
                        
                        {/* Quick Info Pills */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '8px', 
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            marginBottom: '20px'
                        }}>
                            <div style={{
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: 500
                            }}>
                                <Mail size={12} style={{ marginRight: '4px' }} />
                                {user.email}
                            </div>
                            <div style={{
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: 500
                            }}>
                                <Calendar size={12} style={{ marginRight: '4px' }} />
                                {user.createdDate}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* User Information Card */}
                <Card 
                    style={{ 
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                        border: 'none',
                        flex: '1 1 400px',
                        minWidth: '400px'
                    }}
                >
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '28px'
                    }}>
                        <Title level={3} style={{ 
                            margin: 0, 
                            color: '#1f2937',
                            fontWeight: 600
                        }}>
                            User Information
                        </Title>
                    </div>
                    
                    <Descriptions 
                        bordered 
                        column={1}
                        size="middle"
                        labelStyle={{ width: '30%', fontWeight: 500, background: '#fafafa' }}
                        contentStyle={{ background: '#fff' }}
                        style={{ borderRadius: '8px' }}
                    >
                        <Descriptions.Item label="Email">
                            <span>{user.email}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Username">
                            <span>{user.username}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="External Ref">
                            <span>{user.externalRef}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Created Date">
                            <span>{user.createdDate}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Is Super Admin">
                            <span>{user.isSuperAdmin ? 'Yes' : 'No'}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Role Type">
                            <span>{user.roleType}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Is Logged In">
                            <span>{user.isLoggedIn ? 'Yes' : 'No'}</span>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            </div>
        </div>
    );
};

export default ViewModuleUser;